import React, { useState } from 'react';
import { mergeStyleSets } from '@fluentui/react';
import { Stack, CaretUp, CaretDown } from '@phosphor-icons/react';
import { boroondaraPalette } from '../../theme/boroondaraTheme';
import { SpatialLayer, FilterChip } from './types';
import { LAYER_FILTERS } from './constants';

const p = boroondaraPalette;

interface LayerPanelProps {
  layers: SpatialLayer[];
  activeLayers: Set<string>;
  onToggle: (layerId: string) => void;
  choroplethEnabled: boolean;
  onToggleChoropleth: () => void;
  activeFilters: Record<string, string>;
  onFilterClick: (layerCode: string, value: string) => void;
}

const styles = mergeStyleSets({
  panel: {
    background: '#FFFFFF',
    borderRadius: 4,
    border: `1px solid ${p.border}`,
    marginBottom: 12,
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 16px',
    cursor: 'pointer',
    selectors: { ':hover': { background: '#FAFAFA' } },
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12,
    fontWeight: 600,
    color: p.text,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  } as any,
  activeCount: {
    fontSize: 11,
    fontWeight: 500,
    color: '#fff',
    background: p.primary,
    borderRadius: 999,
    padding: '1px 7px',
    marginLeft: 6,
  },
  body: {
    borderTop: `1px solid ${p.borderSoft}`,
    padding: '10px 12px',
  },
  grid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
  } as any,
  toggle: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    padding: '5px 10px',
    borderRadius: 4,
    border: `1px solid ${p.borderSoft}`,
    background: '#fff',
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 500,
    color: p.text2,
    transition: 'all 0.15s',
    whiteSpace: 'nowrap',
    selectors: { ':hover': { borderColor: p.border, background: '#FAFAFA' } },
  } as any,
  toggleActive: {
    borderColor: p.primary,
    background: p.selectedBg,
    color: p.text,
  },
  swatch: {
    width: 8,
    height: 8,
    borderRadius: 2,
    flexShrink: 0,
  },
  featureCount: {
    fontSize: 10,
    color: p.text3,
    marginLeft: 2,
  },
  censusToggle: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    padding: '5px 10px',
    borderRadius: 4,
    border: `1px dashed ${p.borderSoft}`,
    background: '#fff',
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 500,
    color: p.text2,
    transition: 'all 0.15s',
    whiteSpace: 'nowrap',
    selectors: { ':hover': { borderColor: p.primary, background: '#FAFAFA' } },
  } as any,
  censusActive: {
    borderColor: p.primary,
    borderStyle: 'solid',
    background: p.selectedBg,
    color: p.text,
  },
  chipSection: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
    paddingTop: 8,
    borderTop: `1px solid ${p.borderSoft}`,
    marginTop: 8,
  } as any,
  chipLabel: {
    fontSize: 10,
    fontWeight: 600,
    color: p.text3,
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
    marginRight: 4,
    alignSelf: 'center',
  } as any,
  chip: {
    fontSize: 10,
    fontWeight: 500,
    padding: '2px 8px',
    borderRadius: 999,
    cursor: 'pointer',
    border: `1px solid ${p.borderSoft}`,
    background: '#fff',
    color: p.text2,
    transition: 'all 0.15s',
    selectors: { ':hover': { borderColor: p.primary } },
  } as any,
});

export default function LayerPanel({
  layers,
  activeLayers,
  onToggle,
  choroplethEnabled,
  onToggleChoropleth,
  activeFilters,
  onFilterClick,
}: LayerPanelProps) {
  const [expanded, setExpanded] = useState(true);
  const activeCount = activeLayers.size + (choroplethEnabled ? 1 : 0);

  // Collect filter chips for active layers
  const activeChips: Array<{ layerCode: string; layerName: string; chips: FilterChip[] }> = [];
  layers.forEach((layer) => {
    if (!activeLayers.has(layer.layer_id)) return;
    const chips = LAYER_FILTERS[layer.code];
    if (chips?.length) {
      activeChips.push({ layerCode: layer.code, layerName: layer.name, chips });
    }
  });

  return (
    <div className={styles.panel}>
      <div className={styles.header} onClick={() => setExpanded((v) => !v)}>
        <div className={styles.headerLeft}>
          <Stack size={13} weight="bold" style={{ color: p.primary }} />
          Map Layers
          {activeCount > 0 && <span className={styles.activeCount}>{activeCount}</span>}
        </div>
        {expanded
          ? <CaretUp size={12} weight="bold" style={{ color: p.text3 }} />
          : <CaretDown size={12} weight="bold" style={{ color: p.text3 }} />
        }
      </div>

      {expanded && (
        <div className={styles.body}>
          <div className={styles.grid}>
            {layers.map((layer) => {
              const isActive = activeLayers.has(layer.layer_id);
              return (
                <div
                  key={layer.layer_id}
                  className={`${styles.toggle} ${isActive ? styles.toggleActive : ''}`}
                  onClick={() => onToggle(layer.layer_id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') onToggle(layer.layer_id); }}
                >
                  <div
                    className={styles.swatch}
                    style={{ background: isActive ? layer.color : '#D0D0D0' }}
                  />
                  {layer.name}
                  <span className={styles.featureCount}>{layer.feature_count}</span>
                </div>
              );
            })}

            {/* Census Data toggle */}
            <div
              className={`${styles.censusToggle} ${choroplethEnabled ? styles.censusActive : ''}`}
              onClick={onToggleChoropleth}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') onToggleChoropleth(); }}
            >
              <div
                className={styles.swatch}
                style={{
                  background: choroplethEnabled
                    ? 'linear-gradient(135deg, #d9f4d7, #0d3a1c)'
                    : '#D0D0D0',
                }}
              />
              Census Data
            </div>
          </div>

          {/* Filter chips for active layers */}
          {activeChips.length > 0 && (
            <div className={styles.chipSection}>
              {activeChips.map(({ layerCode, layerName, chips }) => (
                <React.Fragment key={layerCode}>
                  <span className={styles.chipLabel}>{layerName.split(' ')[0]}:</span>
                  {chips.map((chip) => (
                    <span
                      key={`${layerCode}-${chip.value}`}
                      className={styles.chip}
                      style={
                        activeFilters[layerCode] === chip.value
                          ? { background: p.primary, color: '#fff', borderColor: p.primary }
                          : undefined
                      }
                      onClick={() => onFilterClick(layerCode, chip.value)}
                    >
                      {chip.label}
                    </span>
                  ))}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
